import { createSlice, PayloadAction } from "@reduxjs/toolkit";

/**
 * Business profile state for registration
 * Stores company information and business details
 */
export interface RegistrationBizState {
  company: string | null;
  regNumber: string | null;
  legalStructure: string | null;
  typeOfInstitute: string | null;
  country: string | null;
  address: string | null;
  phoneNumber: string | null;
}

const initialState: RegistrationBizState = {
  company: null,
  regNumber: null,
  legalStructure: null,
  typeOfInstitute: null,
  country: null,
  address: null,
  phoneNumber: null,
};

/**
 * Generic reducer factory for simple field updates
 * Eliminates duplication for setters that just assign a value
 */
const createFieldSetter = <K extends keyof RegistrationBizState>(field: K) => {
  return (state: RegistrationBizState, action: PayloadAction<RegistrationBizState[K]>) => {
    state[field] = action.payload;
  };
};

const bizSlice = createSlice({
  name: "biz",
  initialState,
  reducers: {
    // Business field setters - Unwrapped payload pattern
    setCompanyRedux: createFieldSetter("company"),
    setRegNumberRedux: createFieldSetter("regNumber"),
    setLegalStructureRedux: createFieldSetter("legalStructure"),
    setTypeOfInstituteRedux: createFieldSetter("typeOfInstitute"),
    setCountryRedux: createFieldSetter("country"),
    setAddressRedux: createFieldSetter("address"),
    setPhoneNumberRedux: createFieldSetter("phoneNumber"),

    /**
     * Bulk update multiple fields at once
     * Useful for hydrating state from API response
     */
    hydrateRegistrationBizState: (state, action: PayloadAction<Partial<RegistrationBizState>>) => {
      return { ...state, ...action.payload };
    },

    /**
     * Reset to initial state
     */
    resetRegistrationBizState: () => initialState,
  },
});

export const {
  setCompanyRedux,
  setRegNumberRedux,
  setLegalStructureRedux,
  setTypeOfInstituteRedux,
  setCountryRedux,
  setAddressRedux,
  setPhoneNumberRedux,
  hydrateRegistrationBizState,
  resetRegistrationBizState,
} = bizSlice.actions;

export default bizSlice.reducer;
